import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";

export default function AboutSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-16 sm:py-20 overflow-hidden"
      style={{ backgroundColor: "#FDFBF7" }}
    >
      <div ref={ref} className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.p
          initial={{ opacity: 0, x: -20 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-sm font-bold tracking-[0.2em] uppercase text-[#bb321a] mb-4"
        >
          Who We Are
        </motion.p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-['Syne'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#bb321a] leading-[1.05] mb-8"
            >
              Indigenous expertise.
              <br />
              <span className="text-[#bb321a]">Commercial rigour.</span>
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="space-y-5 text-[#2D2A26] leading-relaxed"
            >
              <p className="text-base sm:text-lg">
                T.I.I.N.A. (Te Pūtahitanga Auaha Taketake) is a network of experienced directors, researchers, strategists, and practitioners who operate at the intersection of culture, commerce, and community. Our directors bring decades of combined experience across law, engineering, research, health systems, and investment.
              </p>
              <p className="text-base sm:text-lg">
                We hold deep relationships with whānau, hapū, iwi, and Pacific communities across Aotearoa, and we bring that relational capital to bear with rigour and commercial discipline. T.I.I.N.A. works in genuine partnership: we bring full capability alongside our partners, and we hold each other to outcomes that are commercially sound, culturally grounded, and built to last.
              </p>
              <p className="text-base sm:text-lg">
                Our work is structured, staged, and accountable. Everything we do falls within three domains that are inseparable in te ao Māori: People, Place, and Planet.
              </p>
            </motion.div>
          </div>

          {/* The Story */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative"
          >
            <div className="bg-[#45443f] p-8 sm:p-10 relative overflow-hidden">
              <div className="relative z-10">
                <div className="w-12 h-1 bg-[#bb321a] mb-6" />
                <h3 className="font-['Syne'] text-2xl sm:text-3xl font-semibold text-white mb-4">
                  The Story of T.I.I.N.A.
                </h3>
                <p className="text-white/70 leading-relaxed text-base sm:text-lg">
                  <strong className="text-white">Strategic Activation:</strong> We move initiatives from aspiration to funded, measurable implementation. <strong className="text-white">Research and Evidence:</strong> Kaupapa Māori research that is published, cited, and capable of shifting systems and policy.
                </p>
                <p className="text-white/70 leading-relaxed mt-4 text-base sm:text-lg">
                  <strong className="text-white">Relationship Capital:</strong> 100,000+ businesses, iwi, and community partners built through decades of presence. <strong className="text-white">Governance and Advisory:</strong> Executive advisory grounded in deep experience across legal, health, engineering, research, and investment.
                </p>
              </div>
              {/* Full-width niho design element */}
              <div className="mt-8 -mx-8 sm:-mx-10 -mb-8 sm:-mb-10">
                <img
                  src="https://customer-assets.emergentagent.com/job_tangata-hub/artifacts/sca9rlrc_niho_element_ma_02.jpg"
                  alt=""
                  aria-hidden="true"
                  className="w-full max-h-32 sm:max-h-48 object-contain"
                  style={{ mixBlendMode: "screen" }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
